import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const SuperAdminContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session-storage helpers (replaces localStorage)
const SA_KEYS = { token: '_sa_at', admin: '_sa_admin', mustChange: '_sa_mcp' };

const getStoredToken = () => sessionStorage.getItem(SA_KEYS.token) || null;
const getStoredAdmin = () => {
  try {
    const v = sessionStorage.getItem(SA_KEYS.admin);
    return v ? JSON.parse(v) : null;
  } catch { return null; }
};

export const SuperAdminProvider = ({ children }) => {
  const tokenRef = useRef(getStoredToken());
  const [admin, setAdmin] = useState(getStoredAdmin);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(
    () => sessionStorage.getItem(SA_KEYS.mustChange) === 'true'
  );
  const [tempToken, setTempToken] = useState(null);

  // Token setter — keeps ref + state + sessionStorage in sync
  const setToken = useCallback((newToken) => {
    tokenRef.current = newToken;
    if (newToken) {
      sessionStorage.setItem(SA_KEYS.token, newToken);
    } else {
      sessionStorage.removeItem(SA_KEYS.token);
    }
  }, []);

  const updateAdmin = useCallback((data) => {
    setAdmin(data);
    if (data) {
      sessionStorage.setItem(SA_KEYS.admin, JSON.stringify(data));
    } else {
      sessionStorage.removeItem(SA_KEYS.admin);
    }
  }, []);

  // Axios instance — always attaches Bearer from ref + sends cookies
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
    });
    instance.interceptors.request.use((config) => {
      const t = tokenRef.current;
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    return instance;
  }, []);

  // Init: validate existing session on mount
  useEffect(() => {
    const token = tokenRef.current;
    const storedAdmin = getStoredAdmin();
    if (token && storedAdmin) {
      setAdmin(storedAdmin);
      if (sessionStorage.getItem(SA_KEYS.mustChange) === 'true') {
        setMustChangePassword(true);
        setTempToken(token);
      }
      api.get('/api/super-admin/auth/me')
        .then(res => updateAdmin(res.data))
        .catch(() => {
          setToken(null);
          updateAdmin(null);
          sessionStorage.removeItem(SA_KEYS.mustChange);
          setMustChangePassword(false);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/api/super-admin/auth/login', { email, password });
    if (!res.data.requires_2fa) {
      setToken(res.data.access_token);
      updateAdmin(res.data.admin);

      if (res.data.must_change_password) {
        sessionStorage.setItem(SA_KEYS.mustChange, 'true');
        setMustChangePassword(true);
        setTempToken(res.data.access_token);
      } else {
        sessionStorage.removeItem(SA_KEYS.mustChange);
        setMustChangePassword(false);
      }
    }
    return res.data;
  };

  // Called after 2FA verify succeeds (from SuperAdminLogin)
  const complete2FALogin = useCallback((accessToken, adminData) => {
    setToken(accessToken);
    updateAdmin(adminData);
    sessionStorage.removeItem(SA_KEYS.mustChange);
    setMustChangePassword(false);
  }, [setToken, updateAdmin]);

  const completePasswordChange = useCallback(() => {
    sessionStorage.removeItem(SA_KEYS.mustChange);
    setMustChangePassword(false);
    setTempToken(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/super-admin/auth/logout');
    } catch { /* proceed with local cleanup */ }
    sessionStorage.removeItem(SA_KEYS.token);
    sessionStorage.removeItem(SA_KEYS.admin);
    sessionStorage.removeItem(SA_KEYS.mustChange);
    tokenRef.current = null;
    setAdmin(null);
    setMustChangePassword(false);
    setTempToken(null);
  }, [api]);

  // Start impersonating a support agent (admin only)
  const impersonate = useCallback(async (agentId) => {
    const res = await api.post(`/api/super-admin/agents/${agentId}/impersonate`);
    setToken(res.data.access_token);
    updateAdmin(res.data.admin);
    return res.data.admin;
  }, [api, setToken, updateAdmin]);

  // Return from an impersonation session to the original admin
  const stopImpersonation = useCallback(async () => {
    const res = await api.post('/api/super-admin/auth/stop-impersonation');
    setToken(res.data.access_token);
    updateAdmin(res.data.admin);
    return res.data.admin;
  }, [api, setToken, updateAdmin]);

  return (
    <SuperAdminContext.Provider value={{
      admin,
      loading,
      login,
      logout,
      api,
      mustChangePassword,
      tempToken,
      completePasswordChange,
      complete2FALogin,
      impersonate,
      stopImpersonation,
    }}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error('useSuperAdmin must be used within SuperAdminProvider');
  }
  return context;
};
